import { useState, useEffect } from 'react'
import { 
  User, 
  BarChart3, 
  TrendingUp, 
  Award,
  Activity,
  Edit,
  Settings,
  AlertCircle
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import Navigation from '../components/Navigation'

interface UserStats {
  totalStrategies: number
  liveStrategies: number
  totalTrades: number
  totalPnL: number
  joinDate: string
}

export default function ProfilePage() {
  const { user } = useAuth()
  const [stats, setStats] = useState<UserStats>({
    totalStrategies: 0,
    liveStrategies: 0,
    totalTrades: 0,
    totalPnL: 0,
    joinDate: ''
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      fetchUserStats()
    }
  }, [user])

  const fetchUserStats = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // 获取策略统计
      const { data: strategies, error: strategiesError } = await supabase
        .from('strategies')
        .select('id, status')
        .eq('created_by', user?.id)

      if (strategiesError) throw strategiesError

      const strategyIds = strategies?.map(s => s.id) || []
      
      // 获取交易统计 - 只在有策略时查询
      let totalTrades = 0
      let totalPnL = 0
      
      if (strategyIds.length > 0) {
        const { data: trades, error: tradesError } = await supabase
          .from('manual_test_trades')
          .select('pnl_amount')
          .in('strategy_id', strategyIds)

        if (tradesError) throw tradesError
        
        totalTrades = trades?.length || 0
        totalPnL = trades?.reduce((sum, trade) => sum + (trade.pnl_amount || 0), 0) || 0
      }

      const totalStrategies = strategies?.length || 0
      const liveStrategies = strategies?.filter(s => s.status === 'live').length || 0

      setStats({
        totalStrategies,
        liveStrategies,
        totalTrades,
        totalPnL,
        joinDate: user?.created_at ? new Date(user.created_at).toLocaleDateString('zh-CN') : ''
      })
    } catch (err) {
      console.error('Error fetching user stats:', err)
      setError('加载用户统计数据失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-violet-400"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* 页面头部 */}
        <div className="card mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-violet-500/20 rounded-full flex items-center justify-center border border-violet-500/30">
              <User className="w-8 h-8 sm:w-10 sm:h-10 text-violet-400" />
            </div>
            <div className="ml-4 sm:ml-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-gradient mb-2">
                {user?.username || user?.email || '用户'}
              </h1>
              <p className="text-sm sm:text-base text-white/70">
                加入时间: {stats.joinDate}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <Link to="/settings" className="btn-secondary flex items-center justify-center sm:justify-start">
              <Settings className="w-4 h-4 mr-2" />
              设置
            </Link>
            <button className="btn-primary flex items-center justify-center sm:justify-start">
              <Edit className="w-4 h-4 mr-2" />
              编辑资料
            </button>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="card mb-4 sm:mb-6">
          <div className="flex items-center p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
        <div className="card">
          <div className="flex items-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-500/30">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-white/60 text-xs sm:text-sm">总策略数</p>
              <p className="text-xl sm:text-2xl font-bold text-white">{stats.totalStrategies}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center border border-green-500/30">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-white/60 text-sm">实盘策略</p>
              <p className="text-2xl font-bold text-white">{stats.liveStrategies}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center border border-purple-500/30">
              <Activity className="w-6 h-6 text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-white/60 text-sm">总交易数</p>
              <p className="text-2xl font-bold text-white">{stats.totalTrades}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center border border-orange-500/30">
              <Award className="w-6 h-6 text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-white/60 text-sm">总盈亏</p>
              <p className={`text-2xl font-bold ${stats.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.totalPnL >= 0 ? '+' : ''}{stats.totalPnL.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 最近活动 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">最近活动</h3>
          <div className="space-y-4">
            <div className="flex items-center p-3 bg-white/5 rounded-lg">
              <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-violet-400" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-white font-medium">创建了新策略</p>
                <p className="text-white/60 text-sm">2小时前</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-white/5 rounded-lg">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-white font-medium">策略开始实盘交易</p>
                <p className="text-white/60 text-sm">1天前</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-white/5 rounded-lg">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Activity className="w-4 h-4 text-blue-400" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-white font-medium">完成回测分析</p>
                <p className="text-white/60 text-sm">3天前</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-medium text-white mb-4">快速操作</h3>
          <div className="space-y-3">
            <Link
              to="/strategy/new"
              className="flex items-center p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-violet-400" />
              </div>
              <div className="ml-3">
                <p className="text-white font-medium">创建新策略</p>
                <p className="text-white/60 text-sm">开始设计您的交易策略</p>
              </div>
            </Link>
            <Link
              to="/strategies"
              className="flex items-center p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-white font-medium">查看策略列表</p>
                <p className="text-white/60 text-sm">管理您的所有策略</p>
              </div>
            </Link>
            <Link
              to="/settings"
              className="flex items-center p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
            >
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                <Settings className="w-4 h-4 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-white font-medium">账户设置</p>
                <p className="text-white/60 text-sm">管理您的账户偏好</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* 成就徽章 */}
      <div className="card mt-6">
        <h3 className="text-lg font-medium text-white mb-4">成就徽章</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-yellow-500/30">
              <Award className="w-6 h-6 text-yellow-400" />
            </div>
            <p className="text-white font-medium text-sm">策略大师</p>
            <p className="text-white/60 text-xs">创建了3个策略</p>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-green-500/30">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <p className="text-white font-medium text-sm">实盘交易者</p>
            <p className="text-white/60 text-xs">启动了实盘策略</p>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg opacity-50">
            <div className="w-12 h-12 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-gray-500/30">
              <Activity className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-white font-medium text-sm">交易专家</p>
            <p className="text-white/60 text-xs">完成100笔交易</p>
          </div>
          <div className="text-center p-4 bg-white/5 rounded-lg opacity-50">
            <div className="w-12 h-12 bg-gray-500/20 rounded-full flex items-center justify-center mx-auto mb-2 border border-gray-500/30">
              <Award className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-white font-medium text-sm">盈利王者</p>
            <p className="text-white/60 text-xs">累计盈利1000+</p>
          </div>
        </div>
      </div>
      </main>
    </div>
  )
} 